import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Downshift from 'downshift';
import '../../utils/assertions.es.js';
import { warn, runIfFn } from '../../utils/functions.es.js';
import '../../utils/responsive.es.js';
import '../../utils/scrollIntoView.es.js';
import { themeMode, shadow, palette } from '../../utils/theme.es.js';
import '../../utils/useEnhancedEffect.es.js';
import 'react-is';
import styled from 'styled-components';
import { Box } from '../../primitives/Box/Box.es.js';
import { List } from '../List/List.es.js';
import { Styled } from './styled.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const defaultGetItemsLabel = (item) => `${item}`;
const DefaultComponent = ({
  getItemsLabel = defaultGetItemsLabel,
  children
}) => /* @__PURE__ */ React.createElement(React.Fragment, null, getItemsLabel(children));
const DefaultNoResultsComponent = ({ children }) => /* @__PURE__ */ React.createElement(Box, {
  p: "md"
}, children);
const Popover = styled(Box)`
    background-color: ${themeMode({
  light: palette("neutral.0"),
  dark: palette("neutral.800")
})};
    box-shadow: ${shadow("3")};
    overflow-y: auto;
    width: 100%;
    margin: 0;
    border-top: 0;
    position: absolute;
    z-index: 1000;
    list-style: none;
    padding: 0;
    left: 0;
    border-radius: 0;
`;
const getGroups = (groupBy, items) => groupBy ? Array.from(new Set(items.map((item) => groupBy(item)))) : [""];
const getItemFromItemWithIndex = (item) => item.item;
const getItemsByGroup = (groupBy, groups, items) => groups.map((group) => ({
  group,
  items: groupBy ? items.filter((item) => groupBy(getItemFromItemWithIndex(item)) === group) : items
}));
const Autocomplete = (_a) => {
  var _b = _a, {
    children,
    id,
    items = [],
    onChange,
    initialSelectedItem = [],
    initialInputValue = "",
    singleValueComponent,
    itemComponent: ItemComponent = DefaultComponent,
    noResultsComponent: NoResultsComponent = DefaultNoResultsComponent,
    staticBottomComponent,
    openOnFocus = false,
    groupBy,
    getItemsLabel = defaultGetItemsLabel,
    downshiftProps,
    menuProps
  } = _b, props = __objRest(_b, [
    "children",
    "id",
    "items",
    "onChange",
    "initialSelectedItem",
    "initialInputValue",
    "singleValueComponent",
    "itemComponent",
    "noResultsComponent",
    "staticBottomComponent",
    "openOnFocus",
    "groupBy",
    "getItemsLabel",
    "downshiftProps",
    "menuProps"
  ]);
  warn({
    condition: true,
    message: "The Autocomplete component will be deprecated in a future version. Use ComboBox or MultiSelect."
  });
  const groups = useMemo(() => getGroups(groupBy, items), [items, groupBy]);
  const itemsWithIndexes = useMemo(() => items.map((item, index) => ({
    item,
    index: groups.length + index
  })), [items, groups.length]);
  const itemsByGroup = useMemo(() => getItemsByGroup(groupBy, groups, itemsWithIndexes), [groupBy, groups, itemsWithIndexes]);
  const [inputItems, setInputItems] = useState(itemsByGroup);
  const [selectedItems, setSelectedItems] = useState(initialSelectedItem);
  const [inputValue, setInputValue] = useState(initialInputValue);
  const [isSingleInput, setIsSingleInput] = useState();
  const [isSingleValueComponentVisible, setIsSingleValueComponentVisible] = useState(false);
  const filterItems = useCallback((inputValue2) => {
    setInputItems(() => itemsByGroup.reduce((items2, groupData) => {
      const filteredItems = groupData.items.filter((i) => typeof getItemsLabel(getItemFromItemWithIndex(i)) === "string" && getItemsLabel(getItemFromItemWithIndex(i)).toLowerCase().includes(inputValue2.toLowerCase()));
      if (filteredItems.length !== 0) {
        return [
          ...items2,
          {
            group: groupData.group,
            items: filteredItems
          }
        ];
      }
      return items2;
    }, []));
  }, [getItemsLabel, itemsByGroup]);
  useEffect(() => {
    filterItems(inputValue);
  }, [filterItems, inputValue, itemsByGroup]);
  useEffect(() => {
    if (initialSelectedItem.length !== 0 || initialInputValue || singleValueComponent) {
      setIsSingleValueComponentVisible(true);
    }
  }, [initialSelectedItem, initialInputValue, singleValueComponent]);
  useEffect(() => {
    if (onChange) {
      onChange(selectedItems);
    }
  }, [onChange, selectedItems]);
  useEffect(() => {
    const child = React.Children.only(children);
    if (!React.isValidElement(child)) {
      return;
    }
    const { displayName } = child.type;
    if (displayName === "Input") {
      setIsSingleInput(true);
    } else if (displayName === "ChipInput") {
      setIsSingleInput(false);
    }
  }, [children]);
  const addSelectedItem = (item) => {
    if (!item) {
      return;
    }
    if (isSingleInput) {
      setSelectedItems(item);
    } else {
      setSelectedItems([...selectedItems, item]);
      setInputValue("");
    }
  };
  const removeItem = (item) => {
    if (!Array.isArray(selectedItems)) {
      return;
    }
    setSelectedItems(() => selectedItems.filter((i) => i !== item));
    setInputValue("");
  };
  const handleChange = (selectedItem) => {
    addSelectedItem(selectedItem);
    if (singleValueComponent) {
      setIsSingleValueComponentVisible(true);
    }
  };
  const handleInputValueChange = (inputValue2 = "") => {
    setInputValue(inputValue2);
    filterItems(inputValue2);
  };
  const renderInputChild = (inputProps) => {
    const child = React.Children.only(children);
    if (!React.isValidElement(child)) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null);
    }
    const handleOnRemove = (item) => {
      var _a2, _b2;
      removeItem(item);
      (_b2 = (_a2 = child.props).onRemove) == null ? void 0 : _b2.call(_a2, item);
    };
    const handleOnFocus = (e) => {
      var _a2, _b2, _c;
      if (singleValueComponent) {
        setIsSingleValueComponentVisible(false);
      }
      if (openOnFocus) {
        (_a2 = inputProps.onFocus) == null ? void 0 : _a2.call(inputProps, e);
      }
      (_c = (_b2 = child.props).onFocus) == null ? void 0 : _c.call(_b2, e);
    };
    const handleOnBlur = (e) => {
      var _a2;
      if (inputProps.value && singleValueComponent) {
        setIsSingleValueComponentVisible(true);
      }
      (_a2 = inputProps.onBlur) == null ? void 0 : _a2.call(inputProps, e);
    };
    const handleInputRef = (node) => {
      if (!node) {
        return;
      }
      if (!!inputValue && singleValueComponent && isSingleValueComponentVisible) {
        node.style.opacity = "0";
      } else {
        node.style.opacity = "1";
      }
    };
    const { displayName } = child.type;
    if (displayName === "Input") {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, !!inputProps.value && isSingleValueComponentVisible && /* @__PURE__ */ React.createElement(Styled.SingleValueComponentContainer, null, runIfFn(singleValueComponent, { value: inputProps.value })), React.cloneElement(child, __spreadProps(__spreadValues(__spreadValues({}, child.props), inputProps), {
        ref: handleInputRef,
        onFocus: handleOnFocus,
        onBlur: handleOnBlur
      })));
    }
    if (displayName === "ChipInput") {
      return React.cloneElement(child, __spreadProps(__spreadValues(__spreadProps(__spreadValues(__spreadValues({
        getItemsLabel
      }, child.props), inputProps), {
        onFocus: handleOnFocus
      }), !(downshiftProps == null ? void 0 : downshiftProps.selectedItem) ? { values: selectedItems } : {}), {
        onRemove: handleOnRemove
      }));
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null);
  };
  return /* @__PURE__ */ React.createElement(Downshift, __spreadValues({
    onChange: handleChange,
    onInputValueChange: handleInputValueChange,
    initialSelectedItem,
    initialInputValue,
    inputValue,
    inputId: id,
    itemToString: getItemsLabel
  }, downshiftProps), ({
    isOpen,
    highlightedIndex,
    getRootProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    openMenu,
    closeMenu
  }) => /* @__PURE__ */ React.createElement(Styled.Autocomplete, __spreadValues(__spreadValues({}, getRootProps()), props), renderInputChild(__spreadValues({}, getInputProps())), /* @__PURE__ */ React.createElement(Popover, __spreadValues({}, getMenuProps()), isOpen && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, {
    maxHeight: (menuProps == null ? void 0 : menuProps.maxHeight) || 180,
    overflow: "auto"
  }, inputItems.length === 0 ? /* @__PURE__ */ React.createElement(NoResultsComponent, null, "No results match this query") : /* @__PURE__ */ React.createElement(List, null, inputItems.map((groupData, index) => /* @__PURE__ */ React.createElement(React.Fragment, {
    key: index
  }, groupData.group && /* @__PURE__ */ React.createElement(List.Subheader, __spreadValues({}, getItemProps({
    item: null,
    index
  })), groupData.group), groupData.items.map((item) => /* @__PURE__ */ React.createElement(List.Item, __spreadValues({
    key: item.index,
    isSelected: item.index === highlightedIndex
  }, getItemProps({
    item: getItemFromItemWithIndex(item),
    index: item.index
  })), /* @__PURE__ */ React.createElement(ItemComponent, {
    getItemsLabel
  }, getItemFromItemWithIndex(item)))))))), staticBottomComponent && /* @__PURE__ */ React.createElement("div", __spreadValues({}, getItemProps({
    item: null,
    index: -1
  })), /* @__PURE__ */ React.createElement(Styled.Divider, null), runIfFn(staticBottomComponent, {
    inputValue,
    closeMenu
  }))))));
};
Autocomplete.displayName = "Autocomplete";

export { Autocomplete };
//# sourceMappingURL=Autocomplete.es.js.map
